/**
 * NavMenu Component
 *
 * Responsive navigation drawer with theme-aware styling.
 * Supports desktop (permanent) and mobile (swipeable) variants.
 *
 * Uses theme tokens for colors to support all 4 theme variants.
 */

import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MuiDrawer from '@mui/material/Drawer';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Box from '@mui/material/Box';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { NavList } from './NavList';
import process from 'process';
import Logo from '../../UI/AppBars/Logo/EvoraNM.png';

export default function NavMenu(props) {
  const { clicked, drawerWidth, open } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    backgroundColor: theme.palette.background?.nav || theme.palette.background.paper,
    borderRight: 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '2px 0 8px rgba(0, 0, 0, 0.3)'
      : '2px 0 8px rgba(0, 0, 0, 0.08)',
  });

  const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
    },
    backgroundColor: theme.palette.background?.nav || theme.palette.background.paper,
    borderRight: 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '2px 0 8px rgba(0, 0, 0, 0.3)'
      : '2px 0 8px rgba(0, 0, 0, 0.08)',
  });

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    height: 56,
    padding: theme.spacing(0, 2),
    boxSizing: 'border-box',
  }));

  const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      }),
      ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      }),
    }),
  );

  const CloseButton = styled(IconButton)(({ theme }) => ({
    marginLeft: 'auto',
    backgroundColor: theme.palette.background?.navHover || 'rgba(0, 0, 0, 0.04)',
    borderRadius: '10px',
    padding: theme.spacing(0.5),
    transition: theme.transitions.create(['background-color', 'transform'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: theme.palette.background?.navActive || 'rgba(0, 0, 0, 0.08)',
      transform: 'scale(1.05)',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
      fontSize: '1.15rem',
    },
  }));

  const LogoContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      opacity: open ? 1 : 0,
      transition: theme.transitions.create('opacity', {
        duration: theme.transitions.duration.short,
      }),
      '& img': {
        display: 'block',
        height: 26,
        width: 'auto',
        objectFit: 'contain',
        margin: 0,
        verticalAlign: 'middle',
      },
    }),
  );

  const StyledDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(0.5, 2),
    borderColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.08)',
  }));

  const NavScrollContainer = styled(Box)(({ theme }) => ({
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: 1,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.2)',
      borderRadius: 3,
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.3)'
          : 'rgba(0, 0, 0, 0.3)',
      },
    },
  }));

  const desktopMenu = (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader>
        <LogoContainer open={open}>
          <img src={Logo} height="26" width="auto" alt="Evora Logo" />
        </LogoContainer>
        <CloseButton onClick={clicked} aria-label="close drawer">
          <ChevronLeftIcon
            sx={{
              transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.short,
              }),
            }}
          />
        </CloseButton>
      </DrawerHeader>
      <StyledDivider />
      <NavScrollContainer>
        <NavList open={open} isMobile={isMobile} onExpandDrawer={() => !open && clicked()} />
      </NavScrollContainer>
    </Drawer>
  );

  const mobileMenu = (
    <SwipeableDrawer
      anchor="left"
      sx={{
        display: { xs: 'block', sm: 'block' },
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: drawerWidth,
          backgroundColor: theme.palette.background?.nav || theme.palette.background.paper,
          borderRight: 'none',
        },
      }}
      disableBackdropTransition={!iOS}
      disableDiscovery={iOS}
      ModalProps={{
        keepMounted: true,
      }}
      onClose={clicked}
      onOpen={clicked}
      open={open}
      variant="temporary"
    >
      <DrawerHeader>
        <LogoContainer sx={{ opacity: 1 }}>
          <img src={Logo} height="26" width="auto" alt="Evora Logo" />
        </LogoContainer>
        <CloseButton onClick={clicked} aria-label="close drawer">
          <ChevronLeftIcon />
        </CloseButton>
      </DrawerHeader>
      <StyledDivider />
      <NavScrollContainer>
        <NavList open={open} isMobile={isMobile} onExpandDrawer={() => !open && clicked()} />
      </NavScrollContainer>
    </SwipeableDrawer>
  );

  if (isMobile) {
    return mobileMenu;
  } else {
    return desktopMenu;
  }
}
